import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, take } from 'rxjs/operators';
import { environment } from 'oneok/environments/environment';
import { Observable, throwError } from 'rxjs';
import { AuthService } from '@customer/services';
import { Account as AccountService } from '@customer/account/account.svc';
import { SessionAuth } from '@legacy-models/auth/user';
import { LoggedInUser } from '@legacy-models/portal/Account';
import { Contact as ContactModel, PortalRoles } from '@legacy-models/portal/Contact';
import { PortalRoleV2 } from '@legacy-models/portal/PortalRole';
import { AppUser } from '@legacy-models/strapi/AppUser';
import posthog from 'posthog-js';
import { LightboxService, SpinnerService, StorageService, StrapiService, ToastService } from '@core/services';

type AuthContact = Omit<ContactModel, 'PortalRoles' | 'PortalAccountRoles'> & {
  Email: string;
  FirstName: string;
  Id: string;
  IsEmployee: boolean;
  LastName: string;
  PortalAccountRoles?: PortalRoleV2[];
  PortalRoles?: PortalRoles[];
};

type AuthResponse = Omit<SessionAuth, 'Contact'> & { Contact: AuthContact };

type AuthenticatedSession = SessionAuth & { Contact: AuthContact };
type ContactUpdate = Record<string, string>;

@Injectable({
  providedIn: 'root'
})
export class Contact {
  baseUrl = environment.apiUrlV2;
  private readonly _http = inject(HttpClient);
  readonly authSvc = inject(AuthService);
  readonly storageSvc = inject(StorageService);
  private readonly _strapiSvc = inject(StrapiService);
  private readonly _accountSvc = inject(AccountService);
  private readonly _lightboxSvc = inject(LightboxService);
  private readonly _spinnerSvc = inject(SpinnerService);
  private readonly _toastSvc = inject(ToastService);

  // Below code is used to get the contactBy Id
  getContact(contactEmail: string): Observable<any> {
    return this._http.get(this.baseUrl + '/contacts/email/' + contactEmail);
  }

  getAccountModulesForContact(accountId: string, contactEmail: string): Observable<any> {
    return this._http.get<unknown[]>(`${this.baseUrl}/contacts/email/${contactEmail}/accounts/${accountId}/modules`);
  }

  switchCurrentAccount(acctId: string, currUser: AuthenticatedSession): void {
    this._toastSvc.dismissAll();
    this._http.put(`${this.baseUrl}/contacts/email/${currUser.Contact.Email}/accountselected/${acctId}`, {}).subscribe(
      () => {
        currUser.HasAccountSelected = true;
        currUser.CurrAccountId = acctId;
        this._accountSvc.initializeSessionAccount(currUser, 'switch');
        this.getAccountModulesForContact(currUser.CurrAccountId, currUser.Contact.Email).subscribe(modules => {
          currUser.Contact.Modules = modules as any[];
          this.storageSvc.setCurrentUser(currUser);
        });
      },
      () => {
        this._spinnerSvc.hideSpinnerButton();
        setTimeout(() => {
          this._lightboxSvc.close();
          this._toastSvc.alert(
            'Company Selection',
            'An error occurred trying to update your <em>selected</em> company.',
            {
              shouldShowHelpCenter: true,
              shouldShowServiceDesk: true
            }
          );
        }, 1000);
      }
    );
  }

  updateContact(contact: ContactUpdate): Observable<any> {
    [{ oldProp: 'Phone', newProp: 'PhoneNumber' }].forEach(({ oldProp, newProp }) => {
      if (Object.hasOwn(contact, oldProp)) {
        const descriptor = Object.getOwnPropertyDescriptor(contact, oldProp);
        if (descriptor) {
          Object.defineProperty(contact, newProp, descriptor);
          delete contact[oldProp];
        }
      }
    });
    return this._http.put<ContactModel>(this.baseUrl + '/contacts/' + contact.ContactId, contact);
  }

  start(login: LoggedInUser): Observable<void> {
    //post call to PortalApi to login with authenticated user and store User in LS
    return this._http.put<AuthResponse>(this.baseUrl + `/Contacts/email/${login.Email}/login`, login).pipe(
      catchError(error => {
        return throwError(() => error);
      }),
      map(response => {
        if (login.Email.toLocaleLowerCase() !== response.Contact.Email.toLocaleLowerCase()) {
          this.authSvc.logoutUser();
          return;
        }
        const currUser = response.Contact.IsEmployee ? response : this.formatExternalPortalRoles(response);
        if (currUser) {
          posthog.identify(currUser.Contact.Id, {
            email: currUser.Contact.Email.toLowerCase(),
            is_internal_administrator: currUser.Contact?.PortalRoles?.some(portalRole =>
              portalRole.RoleName.includes('ONEOK Admin')
            ),
            name: {
              full: `${currUser.Contact.FirstName} ${currUser.Contact.LastName}`,
              first: currUser.Contact.FirstName,
              last: currUser.Contact.LastName
            }
          });
          this.initializeSessionAuthData(currUser);
          if (response.AppUser) {
            this._strapiSvc
              .initAppUser({
                CrmId: response.AppUser.CrmId,
                Id: response.AppUser.Id
              })
              .then(isSuccess => {
                this.handleCmsUser();
              });
          } else {
            this.handleCmsUser();
          }
        }
      })
    );
  }

  formatExternalPortalRoles(response: AuthResponse): AuthResponse {
    const portalExternalRoles =
      response?.Contact?.PortalAccountRoles?.map((item: PortalRoleV2) => ({
        AccountId: item?.AccountId ?? null,
        BusinessSegment: '',
        Description: item?.PortalRole?.Description ?? null,
        PortalRoleId: item?.PortalRoleId ?? null,
        RoleName: item?.PortalRoleName ?? null,
        Resources: item?.PortalRole?.Resources ?? [],
        AccountName: item?.AccountName ?? null
      })) || [];
    const updatedPayload = {
      Contact: {
        ...response.Contact,
        PortalRoles: portalExternalRoles
      }
    };

    delete updatedPayload.Contact?.PortalAccountRoles;
    return updatedPayload as unknown as AuthResponse;
  }

  /**
   * @description
   */
  handleCmsUser(): void {
    // if( !!this.strapiSvc.notifications?.length === false ) return;
    const handleSuccessfulNotificationsRequest = (
      appNotifications: Awaited<ReturnType<StrapiService['getNotifications']>>
    ): void => {
      this._strapiSvc.notifications = appNotifications.map(
        ({
          id,
          Body: body = '',
          Title: title,
          ViewUrl: url = '',
          IsActive: isActive = true,
          IsViewStatusSaved: isViewStatusSaved = false,
          Meta: meta = []
        }: any) => ({
          id,
          body,
          url,
          isActive,
          // Forcing the `isViewStatusSaved` property to false until API endpoints are created
          isViewStatusSaved: false,
          meta: meta as unknown as [],
          ...(typeof title !== 'undefined'
            ? {
                title
              }
            : {})
        })
      );
    };
    this._strapiSvc.getNotifications().then(handleSuccessfulNotificationsRequest);
  }

  initializeSessionAuthData(currUser: AuthenticatedSession): void {
    const isNoAccountIdGuid = (id?: string): boolean =>
      !id || id === '00000000000000000000000000000000' || id === '00000000-0000-0000-0000-000000000000';
    const hasNoLastSelectedAccount = isNoAccountIdGuid(currUser.Contact.AccountLastSelectedId);

    if (hasNoLastSelectedAccount && !currUser.Contact.IsEmployee) {
      //if external user fetch accountId from PortalAccount Roles
      currUser.CurrAccountId = currUser.Contact?.PortalRoles?.[0]?.AccountId;
      currUser.HasAccountSelected = !isNoAccountIdGuid(currUser.CurrAccountId);
    } else {
      currUser.HasAccountSelected = !hasNoLastSelectedAccount;
      currUser.CurrAccountId = currUser.Contact.AccountLastSelectedId;
    }

    if (currUser.HasAccountSelected) {
      this.getAccountModulesForContact(currUser.CurrAccountId ?? '', currUser.Contact.Email ?? '').subscribe(
        modules => {
          currUser.Contact.Modules = modules as any[];
          this.storageSvc.setCurrentUser(currUser);
          this._accountSvc.initializeSessionAccount(currUser, 'login');
        }
      );
    } else {
      this.storageSvc.setCurrentUser(currUser);
      this._accountSvc.initializeSessionAccount(currUser);
    }
  }

  deleteRoleFromAccountContact(accountId: string, contactEmail: string, roleId: string): Observable<any> {
    return this._http
      .delete<{
        UserMessage: string;
      }>(this.baseUrl + '/Contacts/email/' + contactEmail + '/accounts/' + accountId + '/roles/' + roleId)
      .pipe(take(1));
  }

  addRoleToAccountContact(accountId: string, contactEmail: string, roleId: string): Observable<any> {
    return this._http
      .post(this.baseUrl + '/Contacts/email/' + contactEmail + '/accounts/' + accountId + '/roles/' + roleId, null)
      .pipe(take(1));
  }

  addContactToAccount(
    accountId: string,
    postData: {
      FirstName: string;
      LastName: string;
      Email: string;
      BusinessSegments: string[];
    }
  ): Observable<any> {
    return this._http
      .post<{ ContactId?: string; UserMessage: string }>(this.baseUrl + '/Contacts/accounts/' + accountId, postData)
      .pipe(take(1));
  }
}
